import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import useExperience from 'hooks/Experiences/useExperience'
import { isNonNullable, sum } from 'lib/array/arrayUtils'
import { useMemo, useEffect } from 'react'
import { useUserCartItemState } from './useUserCartItemState'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsSaveForLater } from 'actions/UserCartActions'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'

type UserCartExperienceItemData = {
  experience: App.ExperienceOffer
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined
  loaded: true
  loading: false
  error: any
  itemTotals: App.Checkout.ItemViewTotals
  isDeleted: boolean
  isSavedForLater: boolean
  isBookable: boolean
  isPast: boolean
  isSoldOut: boolean
} | {
  experience: App.ExperienceOffer | undefined
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined
  loaded: false
  loading: boolean
  error: any
  itemTotals: App.Checkout.ItemViewTotals | undefined
  isDeleted: boolean
  isSavedForLater: boolean
  isBookable: boolean
  isPast: boolean
  isSoldOut: boolean
}

function useUserCartExperienceItemData(item: App.Checkout.ExperienceItem): UserCartExperienceItemData {
  const dispatch = useAppDispatch()
  const [experience, experienceLoading, experienceError] = useExperience(item.experienceId)
  const { deleted, saveForLater } = useUserCartItemState(item.itemId)

  const [timeSlots, slotsLoading, slotsError] = useExperienceAvailableTimes(experience, {
    date: item.date,
    isBuyNowBookLater: item.isBuyNowBookLater,
    isGift: item.isGift,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    ticketModesMap: item.ticketModesMap,
  })

  const itemTotals: App.Checkout.ItemViewTotals | undefined = useMemo(() => {
    const hasTickets = timeSlots?.some(slot => slot.tickets.length)

    if (experience && timeSlots && hasTickets) {
      const timeSlotTickets = timeSlots.flatMap(slot => slot.tickets.map(ticket => ({ ...ticket, time: slot.time })))
      const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !item.isBuyNowBookLater && !item.isGift

      const ticketViews = item.tickets.flatMap(ticketItem => {
        const ticket = timeSlotTickets.find(t => {
          return (
            t.id === ticketItem.ticketId &&
            t.date === ticketItem.date &&
            (!requiresTime || t.time == item.time)
          )
        })
        return mapExperienceCartItemToSummaryViews(ticketItem, ticket)
      }).filter(isNonNullable)

      const price = sum(ticketViews, tv => tv.price)
      const value = sum(ticketViews, tv => tv.value)
      const taxesAndFees = sum(ticketViews, tv => tv.taxesAndFees)

      const appPriceTickets = ticketViews.filter(tv => tv.discounts.app.amount > 0)
      const appDiscountAmount = sum(appPriceTickets, tv => tv.discounts.app.amount)
      const appDiscountPercentage = appDiscountAmount > 0 ? sum(appPriceTickets, tv => tv.discounts.app.percentage) / appPriceTickets.length : 0

      return {
        price,
        memberPrice: 0,
        value,
        mobileAppDiscount: {
          amount: appDiscountAmount,
          percentage: appDiscountPercentage,
        },
        taxesAndFees,
        surcharge: 0,
        memberValue: 0,
        extraGuestSurcharge: 0,
      }
    }
  }, [experience, item, timeSlots])

  const isSoldOut = !!experience && experience.status !== 'ONLINE'
  const isPast = dateIsBeforeOrSame(new Date(item.date!), new Date())
  const isBookable = experience?.status === 'ONLINE' && !experienceError && !isPast

  useEffect(() => {
    if (experience && !isBookable && !saveForLater && !experienceLoading && !deleted) {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [isBookable, saveForLater, experienceLoading, dispatch, item.itemId, experience, deleted])

  if (experience && timeSlots && itemTotals) {
    return {
      experience,
      timeSlots,
      loaded: true,
      loading: false,
      error: undefined,
      itemTotals,
      isDeleted: deleted,
      isSavedForLater: saveForLater,
      isBookable,
      isPast,
      isSoldOut,
    }
  }

  const loading = experienceLoading || !!slotsLoading
  const error = experienceError ?? slotsError

  return {
    experience,
    timeSlots,
    loaded: false,
    loading,
    error,
    itemTotals: undefined,
    isDeleted: deleted,
    isSavedForLater: saveForLater,
    isBookable,
    isPast,
    isSoldOut,
  }
}

export default useUserCartExperienceItemData
